import React, { useState } from "react";
import PropTypes from "prop-types";
import { useMutation } from "@apollo/client";
import { Box, Button, Card, IconButton, Typography } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { useModal } from "../../context";
import useCoinDetails from "../../hooks/useCoinDetails";
import TransformImages from "../../utils/TransformImages";
import {
  getDetails,
  hadleAddDiscounts,
  handleDecrementORdelete,
  handleIncrement,
  handleProductList,
} from "../../utils/ProductUtils";
import { ElectronicBillTypes, TAX_CODES } from "../../Enums/ElectronicBill";
import ProductTaxEditor from "../ProductTaxEditor";
import { DELETE_DRAFT_DETAIL, UPDATE_DETAILL_QUANTITY } from "../../graphql";
import { useElectronic } from "../../context/ElectronicContext";

function LineDetailProductCard({
  product,
  lineDetail,
  visualizationType,
  selectables,
  setLineDetail,
}) {
  const { setModalOpen } = useModal();
  const { currentSymbolCoin, toExchangeRate } = useCoinDetails();
  const [updateQuantity] = useMutation(UPDATE_DETAILL_QUANTITY);
  const [deleteDetail] = useMutation(DELETE_DRAFT_DETAIL);
  const { electronicBill } = useElectronic();

  const getUrl = () => {
    const route = product.url?.find(item => item.Route_File !== null);
    return route?.Route_File || "";
  };

  const validateRoute = () => {
    let route = getUrl(product);
    if (route === "") {
      return "https://firebasestorage.googleapis.com/v0/b/tavuel506.appspot.com/o/conta%2Fthumbs%2FNo%20Thumbnail%20Image-min_200x200.jpg?alt=media&token=0315aeb6-211e-4286-be23-39db27a896e7";
    }
    route = TransformImages(product.url, 1);
    return route;
  };

  const handleChangeTax = (value, productData) => {
    if (value < 99999.9999) {
      if (parseFloat(value).toString().split(".")[1]?.length) {
        if (parseFloat(value).toString().split(".")[1]?.length < 5) {
          handleProductList(
            productData.id,
            "BaseTax",
            setLineDetail,
            lineDetail,
            value,
          );
        } else {
          return;
        }
      }
      handleProductList(
        productData.id,
        "BaseTax",
        setLineDetail,
        lineDetail,
        value,
      );
    }
  };

  const isCreditNote = visualizationType === ElectronicBillTypes.CreditNote;
  const isDebitNote = visualizationType === ElectronicBillTypes.DebitNote;
  const isNote = isCreditNote || isDebitNote;

  const [disableIncrementButton, setDisableIncrementButton] =
    useState(isCreditNote);

  const [disableDecrementButton, setDisableDecrementButton] =
    useState(isDebitNote);

  const isIncrementActionValid = ({
    currentProductAmount,
    maxProductAmount,
  }) => {
    const isQuantityEqualToMax = currentProductAmount === maxProductAmount;
    if (isCreditNote && isQuantityEqualToMax) return false;
    return true;
  };
  const isDecrementActionValid = ({
    currentProductAmount,
    maxProductAmount,
  }) => {
    const isOnlyOneProductLeft =
      currentProductAmount === 1 && lineDetail.length === 1;
    const isQuantityEqualToMax = currentProductAmount === maxProductAmount;
    if (
      (isCreditNote && isOnlyOneProductLeft) ||
      (isDebitNote && isQuantityEqualToMax)
    )
      return false;
    return true;
  };

  const handleIncrementSelectedAmount = () => {
    const currentProductAmount = product.quantity;
    const maxProductAmount = product.maxQuantity;
    if (
      !isIncrementActionValid({
        currentProductAmount,
        maxProductAmount,
      })
    ) {
      setDisableIncrementButton(true);
      return;
    }
    handleIncrement(
      product?.id,
      setLineDetail,
      lineDetail,
      selectables,
      updateQuantity,
      electronicBill,
    );
    const newCurrentProductAmount = currentProductAmount + 1;
    setDisableIncrementButton(
      !isIncrementActionValid({
        currentProductAmount: newCurrentProductAmount,
        maxProductAmount,
      }),
    );
    setDisableDecrementButton(
      !isDecrementActionValid({
        currentProductAmount: newCurrentProductAmount,
        maxProductAmount,
      }),
    );
  };

  const handleDecrementSelectedAmount = () => {
    const currentProductAmount = product.quantity;
    const maxProductAmount = product.maxQuantity;
    if (
      !isDecrementActionValid({
        currentProductAmount,
        maxProductAmount,
      })
    ) {
      setDisableDecrementButton(true);
      return;
    }
    handleDecrementORdelete({
      id: product?.id,
      quantity: product?.quantity,
      setDataTable: setLineDetail,
      list: lineDetail,
      selectables,
      updateQuantity,
      electronicBill,
      deleteDetail,
    });
    const newCurrentProductAmount = currentProductAmount - 1;
    setDisableIncrementButton(
      !isIncrementActionValid({
        currentProductAmount: newCurrentProductAmount,
        maxProductAmount,
      }),
    );
    setDisableDecrementButton(
      !isDecrementActionValid({
        currentProductAmount: newCurrentProductAmount,
        maxProductAmount,
      }),
    );
  };

  return (
    <div key={product.id}>
      <Card
        color="primary"
        sx={{
          justifyContent: "center",
          padding: {
            xs: 1,
            md: 2,
          },
          display: "row",
          gap: 1,
        }}
        variant="outlined"
      >
        <div className="card-product">
          <Typography
            className="ellipsis one-line element1"
            typography="modeColor"
            variant="subtitle1"
          >
            {product?.description !== " " ? product?.description : "Sin nombre"}
          </Typography>
          <Typography
            className="element2"
            typography="modeColor"
            variant="subtitle1"
          >
            {currentSymbolCoin}
            {toExchangeRate(parseFloat(product.total)).toFixed(3)}
          </Typography>
          <Typography
            className="ellipsis one-line element3"
            typography="modeColor"
            variant="subtitle1"
          >
            {getDetails(product) || "Sin detalles"}
          </Typography>
          <div
            className="element4"
            style={{ display: "flex", justifyContent: "end" }}
          >
            <Box
              sx={{
                display: "flex",
                gap: "4px",
                maxWidth: "7rem",
                padding: "4px",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #E0E0E0",
              }}
            >
              <IconButton
                disabled={disableDecrementButton}
                sx={{
                  borderRadius: 0,
                  height: "auto",
                  padding: "2px 8px",
                }}
                tabIndex={0}
                onClick={() => handleDecrementSelectedAmount(product)}
                onKeyDown={() => null}
              >
                <FontAwesomeIcon icon="minus" size="sm" />
              </IconButton>
              <Typography typography="modeColor" variant="subtitle1">
                {product.quantity}
              </Typography>
              <IconButton
                disabled={disableIncrementButton}
                sx={{
                  borderRadius: 0,
                  height: "auto",
                  padding: "2px 8px",
                }}
                tabIndex={0}
                onClick={() => handleIncrementSelectedAmount(product)}
                onKeyDown={() => null}
              >
                <FontAwesomeIcon icon="plus" size="sm" />
              </IconButton>
            </Box>
          </div>
          <div
            className="element5"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <img
              alt="product"
              className="imgProduct"
              src={validateRoute(product)}
            />
          </div>

          <div>
            {product?.tax?.MH_Code === TAX_CODES.IVA_CALCULO_ESPECIAL && (
              <div className="product-base ">
                <h2 className="details-product">Base Imponente:</h2>
                <input
                  type="number"
                  value={product?.BaseTax || 0}
                  onChange={e => handleChangeTax(e.target.value, product)}
                />
              </div>
            )}
            {product?.tax?.MH_Code === TAX_CODES.IVA_BIENES_USADOS && (
              <div className="product-base ">
                <Typography typography="modeColor" variant="subtitle1">
                  Monto Factor
                </Typography>
                <input
                  type="number"
                  value={product?.Factor || 0}
                  onChange={e =>
                    handleProductList(
                      product.id,
                      "Factor",
                      setLineDetail,
                      lineDetail,
                      e.target.value,
                    )
                  }
                />
              </div>
            )}
          </div>
          <div
            className="element6"
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "end",
              alignItems: "center",
            }}
          >
            {!isNote && (
              <Button
                color="primary"
                size="small"
                sx={{
                  width: "10rem",
                  marginTop: "1rem",
                  cursor: "pointer",
                }}
                variant="outlined"
                onClick={() =>
                  !isNote &&
                  hadleAddDiscounts(
                    product,
                    setModalOpen,
                    lineDetail,
                    setLineDetail,
                  )
                }
              >
                Descuento
              </Button>
            )}
            <Box
              sx={{
                marginTop: "16px",
                display: "flex",
                gap: "5px",
              }}
            >
              <Typography typography="modeColor" variant="subtitle1">
                {product.billDetailTariff?.Percent ?? product.Tariff.Percent}%
                IVA
              </Typography>
              {!isNote && (
                <IconButton
                  color="default"
                  size="small"
                  sx={{
                    "backgroundColor": "transparent",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                  }}
                  onClick={() =>
                    setModalOpen(
                      true,
                      <ProductTaxEditor
                        defaultTariffId={
                          product.billDetailTariff?.id ?? product.Tariff.id
                        }
                        defaultTaxId={
                          product.billDetailTax?.id || product.tax?.id
                        }
                        onCancelButtonClicked={() => setModalOpen(false)}
                        onSubmitButtonClicked={({
                          selectedTax,
                          selectedTariff,
                        }) => {
                          setLineDetail(prev =>
                            prev.map(prevProduct =>
                              prevProduct.id === product.id
                                ? {
                                    ...prevProduct,
                                    billDetailTax: selectedTax,
                                    billDetailTariff: selectedTariff,
                                  }
                                : prevProduct,
                            ),
                          );
                          setModalOpen(false);
                        }}
                      />,
                    )
                  }
                >
                  <FontAwesomeIcon icon={faPenToSquare} />
                </IconButton>
              )}
            </Box>
          </div>
        </div>
      </Card>
    </div>
  );
}

LineDetailProductCard.propTypes = {
  product: PropTypes.object.isRequired,
  visualizationType: PropTypes.string,
  lineDetail: PropTypes.number.isRequired,
  selectables: PropTypes.bool,
  setLineDetail: PropTypes.func.isRequired,
};
LineDetailProductCard.defaultProps = {
  visualizationType: ElectronicBillTypes.FacturaElectronica,
  selectables: false,
};

export default LineDetailProductCard;
